// Pure, dependency-free aggregation logic for cross-lesson skill progress: given every
// assessment row for one student (any order), selects the latest and previous outcome per
// skill. No React, no Supabase — useStudentSkillProgress fetches the authorized rows
// (RLS-scoped to the calling instructor) and passes them here. Framework-free so the same
// logic is reusable by a future React Native / Expo instructor app without any change.

export type SkillOutcome = 'demonstrated' | 'needs_practice' | 'not_assessed' | 'taught'

export interface AssessmentRecord {
  skillId: string
  skillName: string
  skillCategory: string | null
  outcome: SkillOutcome
  observation: string | null
  updatedAt: string
  lessonId: string | null
  lessonScheduledAt: string | null
}

export interface SkillProgressEntry {
  skillId: string
  skillName: string
  skillCategory: string | null
  latestOutcome: SkillOutcome
  latestObservation: string | null
  latestUpdatedAt: string
  latestLessonId: string | null
  latestLessonScheduledAt: string | null
  previousOutcome: SkillOutcome | null
  previousUpdatedAt: string | null
  timesAssessed: number
}

/**
 * Builds one entry per skill ever assessed: the latest-recorded outcome (by updatedAt,
 * NOT by insertion order — rows may arrive in any order) plus the immediately-preceding
 * one, if any, for a simple historical trend. Re-assessing the same skill in a later
 * lesson creates a new, independent row (migration 057's own design); this function
 * never mutates or merges rows, it only selects which two to surface per skill.
 */
export function buildSkillProgress(rows: AssessmentRecord[]): SkillProgressEntry[] {
  // Sort a COPY, newest first, so this function's result never depends on the order the
  // caller happened to fetch rows in.
  const sorted = [...rows].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())

  const bySkill = new Map<string, SkillProgressEntry>()
  for (const row of sorted) {
    const existing = bySkill.get(row.skillId)
    if (existing) {
      existing.timesAssessed += 1
      if (existing.previousOutcome === null) {
        existing.previousOutcome = row.outcome
        existing.previousUpdatedAt = row.updatedAt
      }
      continue
    }
    bySkill.set(row.skillId, {
      skillId: row.skillId,
      skillName: row.skillName,
      skillCategory: row.skillCategory,
      latestOutcome: row.outcome,
      latestObservation: row.observation,
      latestUpdatedAt: row.updatedAt,
      latestLessonId: row.lessonId,
      latestLessonScheduledAt: row.lessonScheduledAt,
      previousOutcome: null,
      previousUpdatedAt: null,
      timesAssessed: 1,
    })
  }

  return Array.from(bySkill.values())
}
